import React, { createContext, useContext, ReactNode } from "react";
import { useLocation } from "../hooks/useLocation";

// Define the shape of our context data
export interface LocationData {
  latitude: number;
  longitude: number;
  source: "browser" | "ip";
}

// Define what values will be available through the context
interface LocationContextValue {
  locationData: LocationData | null;
  error: Error | null;
  loading: boolean;
  permissionState: PermissionState;
  refreshLocation: () => Promise<void>;
}

// Create the context with a default value
const LocationContext = createContext<LocationContextValue | null>(null);

// Custom hook to use the location context
export function useLocationContext() {
  const context = useContext(LocationContext);

  if (!context) {
    throw new Error("useLocationContext must be used within LocationProvider");
  }

  return context;
}

// Props interface for the provider
interface LocationProviderProps {
  children: ReactNode;
  apiKey: string;
}

// Provider component that wraps the app
export function LocationProvider({ children, apiKey }: LocationProviderProps) {
  // Get all location-related values and functions from our custom hook
  const { locationData, error, loading, permissionState, refreshLocation } =
    useLocation(apiKey);
  // Create the context value object with all needed values and functions
  
  const contextValue: LocationContextValue = {
    locationData,
    error,
    loading,
    permissionState,
    refreshLocation,
  };

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
}
